import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { approveMatchResults } from '../services/matches-api.js';
import ErrorMessage from '../components/common/ErrorMessage.jsx';
import Button from '../components/common/Button.jsx';
import './ReviewMatchPage.css';

export default function ReviewMatchPage() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const match = location.state?.match;
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!match) {
    return (
      <div className="review-match-page">
        <ErrorMessage message="Match data not available. Please navigate from the Captain Dashboard." />
        <Button onClick={() => navigate('/captain/dashboard')}>Back to Dashboard</Button>
      </div>
    );
  }

  const pendingResults = match.pendingResults || [];
  const isConflict = match.status === 'conflict';

  const handleDecision = async (approved) => {
    try {
      setSubmitting(true);
      setError(null);
      const response = await approveMatchResults(matchId, approved, notes || null);
      alert(response.message || (approved ? 'Results approved' : 'Results rejected'));
      navigate('/captain/dashboard');
    } catch (err) {
      setError(err.message || 'Failed to process decision');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="review-match-page">
      <div className="review-header">
        <button className="back-btn" onClick={() => navigate('/captain/dashboard')}>
          &larr; Back
        </button>
        <h1>Review Match Results</h1>
      </div>

      <div className="match-info">
        <h2>{match.team1Name} vs {match.team2Name}</h2>
        {match.scheduledDate && (
          <p className="match-date">
            {match.scheduledDate}{match.scheduledTime ? ` at ${match.scheduledTime}` : ''}
          </p>
        )}
        <span className={`status-badge ${match.status}`}>
          {isConflict ? 'Conflicting Results' : 'Pending Approval'}
        </span>
      </div>

      {isConflict && (
        <div className="conflict-notice">
          Both captains have submitted different results. Please review and decide.
        </div>
      )}

      {pendingResults.length > 0 ? (
        <div className="submissions">
          {pendingResults.map((submission, idx) => (
            <div key={idx} className="submission-card">
              <h3>Submission {idx + 1}</h3>
              <p className="submitted-by">Submitted: {new Date(submission.submittedAt).toLocaleString()}</p>
              <div className="score-summary">
                <span className="team-score">{match.team1Name}: {submission.team1Score}</span>
                <span className="vs">-</span>
                <span className="team-score">{match.team2Name}: {submission.team2Score}</span>
              </div>
              {submission.games && submission.games.length > 0 && (
                <div className="games-list">
                  <h4>Games</h4>
                  {submission.games.map((game, gIdx) => (
                    <div key={gIdx} className="game-row">
                      <span className="game-num">Game {game.gameNumber}</span>
                      <span className={`game-winner ${game.winner}`}>
                        {game.winner === 'team1' ? match.team1Name : match.team2Name} won
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="score-summary standalone">
          <span className="team-score">{match.team1Name}: {match.team1Score ?? 0}</span>
          <span className="vs">-</span>
          <span className="team-score">{match.team2Name}: {match.team2Score ?? 0}</span>
        </div>
      )}

      <div className="review-actions">
        <label className="notes-label" htmlFor="review-notes">Notes (optional)</label>
        <textarea
          id="review-notes"
          className="notes-input"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add any notes about this decision..."
          rows={3}
        />

        {error && <ErrorMessage message={error} />}

        <div className="action-buttons">
          <Button
            variant="danger"
            onClick={() => handleDecision(false)}
            disabled={submitting}
          >
            {submitting ? 'Processing...' : 'Reject Results'}
          </Button>
          <Button
            variant="primary"
            onClick={() => handleDecision(true)}
            disabled={submitting}
          >
            {submitting ? 'Processing...' : 'Approve Results'}
          </Button>
        </div>
      </div>
    </div>
  );
}
